import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { sectionsApi } from '@/services/api';
import { Section, Slide, FullMediaSlide } from '@/types';
import { ANIMATION_CONFIG } from '@/config/animations';
import {
    FullMediaSection,
    TextSection,
    CustomMediaSection,
    SplitSection
} from '@/components/ScrollSections';
import { useImagePreloader, extractImageUrls } from '@/hooks/useImagePreloader';
import { useSectionCursor } from '@/hooks/useSectionCursor';
import { useCursorContext } from '@/providers/CursorProvider';
import { useBackgroundColor } from '@/providers/BackgroundColorProvider';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const Home: React.FC = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { setCursorType } = useCursorContext();
    const { setBackgroundColor } = useBackgroundColor();
    const [sections, setSections] = useState<Section[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [currentChildIndex, setCurrentChildIndex] = useState(0);
    const [animationKey, setAnimationKey] = useState(0); // Force re-render with new key
    const [isImageZoomed, setIsImageZoomed] = useState(false); // Track if any image is zoomed
    const containerRef = useRef<HTMLDivElement>(null);
    const sectionsRefs = useRef<(HTMLDivElement | null)[]>([]);
    const isAnimating = useRef(false);

    // Completely block pull-to-refresh on mobile
    useEffect(() => {
        const preventPullToRefresh = (e: TouchEvent) => {
            // Prevent pull-to-refresh on all touch moves
            const target = e.target as HTMLElement;
            
            // Check if we're at the top of a scrollable element
            const scrollableParent = target.closest('[data-scrollable="true"]');
            if (scrollableParent) {
                const isAtTop = scrollableParent.scrollTop === 0;
                const touch = e.touches[0];
                const deltaY = touch.clientY - (e as any).startY;
                
                // Only prevent if scrolling down while at top
                if (isAtTop && deltaY > 0) {
                    e.preventDefault();
                }
            } else {
                // For non-scrollable areas, always prevent
                e.preventDefault();
            }
        };

        const trackTouchStart = (e: TouchEvent) => {
            const touch = e.touches[0];
            (e as any).startY = touch.clientY;
        };

        // Prevent overscroll behavior via CSS
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';
        
        // Add touch event listeners
        document.addEventListener('touchstart', trackTouchStart, { passive: true });
        document.addEventListener('touchmove', preventPullToRefresh, { passive: false });

        return () => {
            document.body.style.overscrollBehavior = '';
            document.documentElement.style.overscrollBehavior = '';
            document.removeEventListener('touchstart', trackTouchStart);
            document.removeEventListener('touchmove', preventPullToRefresh);
        };
    }, []);

    // Fetch sections from API
    useEffect(() => {
        const fetchSections = async () => {
            setLoading(true);
            try {
                const { data, error } = await sectionsApi.getAll(i18n.language);
                if (data && !error) {
                    const activeSections = data.data
                        .filter((section: Section) => section.is_active)
                        .sort((a: Section, b: Section) => a.order_index - b.order_index);

                    setSections(activeSections);
                }
            } catch (err) {
                console.error('Failed to fetch sections:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSections();
    }, [i18n.language]); // Re-fetch when language changes

    // Preload images for next section to improve loading performance
    const nextSectionImages = useMemo(() => {
        if (!sections.length || currentSectionIndex >= sections.length - 1) return [];

        const nextSection = sections[currentSectionIndex + 1];
        const slides = [];

        if (nextSection.slide) {
            slides.push(nextSection.slide);
        }
        if (nextSection.child_slides && nextSection.child_slides.length > 0) {
            slides.push(...nextSection.child_slides.slice(0, 2)); // Preload first 2 child slides
        }

        return extractImageUrls(slides);
    }, [sections, currentSectionIndex]);

    // Use the preloader hook
    useImagePreloader(nextSectionImages);

    // Update background color when current slide changes
    useEffect(() => {
        if (!sections.length) return;

        const currentSection = sections[currentSectionIndex];
        if (!currentSection) return;

        let currentSlide: Slide;
        if (currentChildIndex === 0) {
            currentSlide = currentSection.slide;
        } else if (currentSection.child_slides && currentSection.child_slides.length > 0) {
            const activeChildren = currentSection.child_slides
                .filter(child => child.is_active)
                .sort((a, b) => a.order_index - b.order_index);
            currentSlide = activeChildren[currentChildIndex - 1]?.slide_data;
        }

        if (currentSlide!) {
            let bgColor: string | undefined;
            switch (currentSlide.layout_type) {
                case 'full-media':
                case 'text-section':
                case 'custom-media':
                    bgColor = currentSlide.background_color;
                    break;
                case 'split-section':
                    // For split sections, use the left background color as default
                    bgColor = currentSlide.left_background_color;
                    break;
            }
            setBackgroundColor(bgColor || '#000');
        }
    }, [sections, currentSectionIndex, currentChildIndex, setBackgroundColor]);

    // Setup GSAP scroll-snap sections (vertical layout)
    useEffect(() => {
        if (!sections.length || loading) return;

        const ctx = gsap.context(() => {
            sectionsRefs.current.forEach((section, index) => {
                if (!section) return;

                gsap.set(section, {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100vh',
                    zIndex: index === currentSectionIndex ? 10 : 1,
                    y: index === currentSectionIndex ? 0 : '100vh', // Position off-screen below
                    opacity: index === currentSectionIndex ? 1 : 1 // Keep visible for smooth transition
                });
            });
        }, containerRef);

        return () => ctx.revert();
    }, [sections, loading, currentSectionIndex]);

    // Navigate to section with animation (VERTICAL slide up/down)
    const navigateToSection = useCallback((targetIndex: number) => {
        if (
            isAnimating.current ||
            targetIndex < 0 ||
            targetIndex >= sections.length ||
            targetIndex === currentSectionIndex
        ) {
            return;
        }

        isAnimating.current = true;
        const currentSection = sectionsRefs.current[currentSectionIndex];
        const targetSection = sectionsRefs.current[targetIndex];

        const isGoingDown = targetIndex > currentSectionIndex;

        const tl = gsap.timeline({
            onComplete: () => {
                isAnimating.current = false;
                setCurrentSectionIndex(targetIndex);
                setCurrentChildIndex(0); // Reset child index on section change
                setIsImageZoomed(false); // Reset zoom state on section change
                setAnimationKey(prev => prev + 1); // Increment key to force re-render
            }
        });

        // Animate current section out (slide up if going down, slide down if going up)
        if (currentSection) {
            tl.to(currentSection, {
                y: isGoingDown ? '-100vh' : '100vh', // Move full viewport height
                opacity: 0,
                duration: ANIMATION_CONFIG.sectionTransition.duration,
                ease: ANIMATION_CONFIG.sectionTransition.ease
            }, 0);
        }

        // Animate target section in (slide from bottom if going down, from top if going up)
        if (targetSection) {
            // IMPORTANT: Reset horizontal position to show main slide (index 0)
            gsap.set(targetSection, {
                zIndex: 11,
                y: isGoingDown ? '100vh' : '-100vh', // Start from opposite side
                opacity: 1,
                x: 0 // Reset horizontal scroll position
            });

            tl.to(targetSection, {
                y: 0,
                duration: ANIMATION_CONFIG.sectionTransition.duration,
                ease: ANIMATION_CONFIG.sectionTransition.ease
            }, 0);
        }
    }, [currentSectionIndex, sections.length]);    // Navigate through child sections (HORIZONTAL left/right)
    const navigateToChild = useCallback((direction: 'next' | 'prev') => {
        const currentSection = sections[currentSectionIndex];
        if (!currentSection.child_slides?.length) return;

        const activeChildren = currentSection.child_slides
            .filter(child => child.is_active)
            .sort((a, b) => a.order_index - b.order_index);

        // overall max index: 0 = main slide, 1..N = child slides
        const maxIndex = activeChildren.length;
        let targetIndex = currentChildIndex;

        if (direction === 'next' && currentChildIndex < maxIndex) {
            targetIndex = currentChildIndex + 1;
        } else if (direction === 'prev' && currentChildIndex > 0) {
            targetIndex = currentChildIndex - 1;
        } else {
            return; // Can't navigate further
        }

        // ALWAYS animate slide transitions (swipe between slides)
        // Note: enable_mobile_animation controls the horizontal auto-scroll WITHIN an image,
        // NOT the swipe transition between slides
        const container = sectionsRefs.current[currentSectionIndex];
        if (container) {
            gsap.to(container, {
                x: -targetIndex * window.innerWidth,
                duration: ANIMATION_CONFIG.childTransition.duration,
                ease: ANIMATION_CONFIG.childTransition.ease,
                onComplete: () => {
                    setCurrentChildIndex(targetIndex);
                    setAnimationKey(prev => prev + 1); // Increment key to force re-render
                }
            });
        }
    }, [currentSectionIndex, currentChildIndex, sections]);

    // Handle zoom state changes from FullMediaSection
    const handleZoomChange = useCallback((zoomed: boolean) => {
        setIsImageZoomed(zoomed);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Block keyboard navigation when image is zoomed
            if (isImageZoomed) {
                return;
            }

            const currentSection = sections[currentSectionIndex];
            const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;

            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    // Up arrow navigates to previous section (VERTICAL)
                    navigateToSection(currentSectionIndex - 1);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    // Down arrow navigates to next section (VERTICAL)
                    if (sections.length > 0 && currentSectionIndex >= sections.length - 1) {
                        navigate('/work');
                    } else {
                        navigateToSection(currentSectionIndex + 1);
                    }
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    // Left arrow navigates to previous child slide (HORIZONTAL)
                    if (hasChildren) {
                        navigateToChild('prev');
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    // Right arrow navigates to next child slide (HORIZONTAL)
                    if (hasChildren) {
                        navigateToChild('next');
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSectionIndex, currentChildIndex, sections, navigateToSection, navigateToChild, isImageZoomed, navigate]);

    // Mouse wheel navigation
    useEffect(() => {
        let wheelTimeout: ReturnType<typeof setTimeout>;

        const handleWheel = (e: WheelEvent) => {
            // Block wheel navigation when image is zoomed
            if (isImageZoomed) {
                return;
            }

            e.preventDefault();

            clearTimeout(wheelTimeout);
            wheelTimeout = setTimeout(() => {
                const currentSection = sections[currentSectionIndex];
                const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;

                // VERTICAL wheel scrolling navigates between SECTIONS (up/down)
                if (e.deltaY !== 0) {
                    if (e.deltaY > 0) {
                        // Scroll down - next section
                        if (sections.length > 0 && currentSectionIndex >= sections.length - 1) {
                            navigate('/work');
                        } else {
                            navigateToSection(currentSectionIndex + 1);
                        }
                    } else {
                        // Scroll up - previous section
                        navigateToSection(currentSectionIndex - 1);
                    }
                }

                // HORIZONTAL wheel scrolling (or trackpad swipe) navigates between CHILD SLIDES (left/right)
                if (hasChildren && e.deltaX !== 0) {
                    if (e.deltaX > 0) {
                        // Scroll right - next child
                        navigateToChild('next');
                    } else {
                        // Scroll left - previous child
                        navigateToChild('prev');
                    }
                }
            }, ANIMATION_CONFIG.wheelDebounce);
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('wheel', handleWheel, { passive: false });
        }

        return () => {
            if (container) {
                container.removeEventListener('wheel', handleWheel);
            }
            clearTimeout(wheelTimeout);
        };
    }, [currentSectionIndex, currentChildIndex, sections, navigateToSection, navigateToChild, isImageZoomed, navigate]);    // Render individual slide based on layout type
    // Touch / swipe navigation for mobile
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let touchStartX = 0;
        let touchStartY = 0;
        let touchStartTime = 0;
        let isSwiping = false;
        let touchMoved = false;
        
        // More sensitive threshold on mobile
        const threshold = window.innerWidth < 768 ? 50 : 60; // px
        const longSwipeThreshold = 120; // px - long swipe to trigger slide navigation on full-media images
        const velocityThreshold = 0.3; // px per ms - faster swipes trigger navigation easier
        const maxSwipeTime = 300; // ms - swipes faster than this are considered quick gestures

        const onTouchStart = (ev: TouchEvent) => {
            const target = ev.target as HTMLElement;
            
            // Check if touch started on an interactive element or within zoom/pan components
            const isInteractive = target.closest(
                'button, a, input, select, textarea, [role="button"], .MuiButtonBase-root, .MuiIconButton-root'
            );
            
            // Check if touch is on a zoom/pan wrapper or strip control
            const isZoomWrapper = target.closest('.react-transform-wrapper, .react-transform-component, [data-strip-control="true"]');
            
            // Don't interfere with interactive elements or zoom components when zoomed
            if (isInteractive || (isZoomWrapper && isImageZoomed)) {
                isSwiping = false;
                return;
            }
            
            const t = ev.touches[0];
            touchStartX = t.clientX;
            touchStartY = t.clientY;
            touchStartTime = Date.now();
            touchMoved = false;
            isSwiping = true;
        };

        const onTouchMove = (ev: TouchEvent) => {
            if (!isSwiping) return;
            
            const t = ev.touches[0];
            const dy = t.clientY - touchStartY;
            const dx = t.clientX - touchStartX;
            const absY = Math.abs(dy);
            const absX = Math.abs(dx);
            
            // Only prevent default for significant movement (swipes), not small movements (taps/clicks)
            // This allows clicks to work while still preventing pull-to-refresh on actual swipes
            const minSwipeThreshold = 15; // pixels - must move at least this much to be considered a swipe
            
            if (absY > minSwipeThreshold || absX > minSwipeThreshold) {
                // Prevent pull-to-refresh when there's actual swipe movement
                if (absY > absX * 0.5) { // Primarily vertical movement
                    ev.preventDefault();
                }
                touchMoved = true;
            }
        };

        const onTouchEnd = (ev: TouchEvent) => {
            if (!isSwiping) {
                return;
            }
            
            isSwiping = false;
            
            // Block all slide navigation when image is zoomed
            if (isImageZoomed) {
                return;
            }

            const t = ev.changedTouches[0];
            const dx = t.clientX - touchStartX;
            const dy = t.clientY - touchStartY;
            const dt = Date.now() - touchStartTime;

            const absX = Math.abs(dx);
            const absY = Math.abs(dy);
            const velocityX = absX / dt;
            const velocityY = absY / dt;

            // Ignore very small movements (accidental touches)
            if (absX < 10 && absY < 10) {
                return;
            }

            // Check if current slide is a FullMediaSection with an image
            const currentSection = sections[currentSectionIndex];
            const currentSlide = currentChildIndex === 0
                ? currentSection?.slide
                : currentSection?.child_slides?.[currentChildIndex - 1]?.slide_data;

            const isFullMediaImage = currentSlide?.layout_type === 'full-media' &&
                (currentSlide as any)?.media_type === 'image';

            // For FullMediaSection images: only navigate on LONG horizontal swipe or fast swipe
            // Short/slow swipes allow image panning
            if (isFullMediaImage && absX > absY) {
                const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;

                // Navigate if swipe is either:
                // 1. Long enough (>= longSwipeThreshold), OR
                // 2. Fast enough (velocity > velocityThreshold) AND within quick swipe time
                const isLongSwipe = absX >= longSwipeThreshold;
                const isFastSwipe = velocityX > velocityThreshold && dt < maxSwipeTime && absX > threshold;
                
                if (hasChildren && (isLongSwipe || isFastSwipe)) {
                    if (dx < 0) {
                        navigateToChild('next');
                    } else {
                        navigateToChild('prev');
                    }
                }
                // Short/slow swipes do nothing (let image pan naturally)
                return;
            }

            // Vertical swipe -> sections
            // Prioritize vertical navigation if movement is clearly vertical
            if (absY > absX * 0.8 && absY > threshold) {
                if (dy < 0) {
                    if (sections.length > 0 && currentSectionIndex >= sections.length - 1) {
                        navigate('/work');
                    } else {
                        navigateToSection(currentSectionIndex + 1);
                    }
                } else {
                    navigateToSection(currentSectionIndex - 1);
                }
                return;
            }

            // Horizontal swipe -> child slides (normal behavior for non-image slides)
            const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;
            if (hasChildren && absX > absY * 0.8 && absX > threshold) {
                if (dx < 0) {
                    navigateToChild('next');
                } else {
                    navigateToChild('prev');
                }
            }
        };

        container.addEventListener('touchstart', onTouchStart, { passive: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false }); // Must be non-passive to prevent pull-to-refresh
        container.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
        };
    }, [sections, currentSectionIndex, currentChildIndex, navigateToSection, navigateToChild, isImageZoomed, navigate]);

    // Handle orientation changes (mobile rotation)
    useEffect(() => {
        const handleOrientationChange = () => {
            // Reset GSAP transforms when device rotates
            sectionsRefs.current.forEach((ref, idx) => {
                if (ref) {
                    if (idx === currentSectionIndex) {
                        gsap.set(ref, { x: -currentChildIndex * window.innerWidth, y: 0, opacity: 1 });
                    } else {
                        gsap.set(ref, { x: 0, y: 0, opacity: 0 });
                    }
                }
            });
        };

        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);

        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleOrientationChange);
        };
    }, [currentSectionIndex, currentChildIndex]);

    // Handle mouse movement for cursor changes in sections with children
    // This must be before the early return to maintain hook order
    useEffect(() => {
        const currentSection = sections[currentSectionIndex];
        const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;

        const isInteractive = (el: Element | null): boolean => {
            if (!el) return false;
            return !!(
                el.closest('a[href], button, [role="button"], input, select, textarea, label, [data-cursor="big"], [data-interactive], [data-clickable], .clickable, .MuiButtonBase-root, .MuiIconButton-root')
            );
        };

        const handleMouseMove = (e: MouseEvent) => {
            // If menu is open, force default and skip arrow logic
            if (document.body.getAttribute('data-menu-open') === 'true') {
                setCursorType('default');
                return;
            }

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const mouseX = e.clientX;
            const mouseY = e.clientY;
            const threshold = 300;

            // Vertical cursor (sections up/down) has priority
            const hasSectionAbove = currentSectionIndex > 0;
            const hasSectionBelow = currentSectionIndex < sections.length - 1;
            const isLastSection = sections.length > 0 && currentSectionIndex >= sections.length - 1;

            if (mouseY <= threshold && hasSectionAbove) {
                setCursorType('arrow-up');
                return;
            }
            if (mouseY >= windowHeight - threshold && (hasSectionBelow || isLastSection)) {
                setCursorType('arrow-down');
                return;
            }

            // If hovering interactive element (and not in vertical edge), show big non-arrow cursor
            const target = e.target as Element | null;
            if (isInteractive(target)) {
                setCursorType('pointer');
                return;
            }

            // Horizontal cursor (child slides left/right) for sections with children
            const currentSectionForCursor = sections[currentSectionIndex];
            const hasChildrenLocal = !!(currentSectionForCursor?.child_slides && currentSectionForCursor.child_slides.length > 0);
            const activeChildrenLocal = hasChildrenLocal
                ? currentSectionForCursor!.child_slides!.filter((child) => child.is_active).sort((a, b) => a.order_index - b.order_index)
                : [] as any[];
            const activeChildrenLen = hasChildrenLocal ? activeChildrenLocal.length : 0;

            if (hasChildrenLocal) {
                const atFirstChild = currentChildIndex === 0;
                const atLastChild = currentChildIndex >= activeChildrenLen;

                if (mouseX <= threshold) {
                    setCursorType(atFirstChild ? 'default' : 'arrow-left');
                } else if (mouseX >= windowWidth - threshold) {
                    setCursorType(atLastChild ? 'default' : 'arrow-right');
                } else {
                    setCursorType('default');
                }
                return;
            }

            // Otherwise default
            setCursorType('default');
        };

        const handleClick = (e: MouseEvent) => {
            // Ignore clicks when hovering interactive elements; their own handlers manage behavior
            const target = e.target as Element | null;
            if (isInteractive(target)) {
                return;
            }
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const mouseX = e.clientX;
            const mouseY = e.clientY;
            const threshold = 300;

            // Vertical click zones for sections
            if (mouseY <= threshold && currentSectionIndex > 0) {
                navigateToSection(currentSectionIndex - 1);
                return;
            }
            if (mouseY >= windowHeight - threshold) {
                if (currentSectionIndex < sections.length - 1) {
                    navigateToSection(currentSectionIndex + 1);
                } else {
                    navigate('/work');
                }
                return;
            }

            // Horizontal click zones for child slides
            const currentSectionForClick = sections[currentSectionIndex];
            const hasChildrenClick = !!(currentSectionForClick?.child_slides && currentSectionForClick.child_slides.length > 0);
            const activeChildrenClick = hasChildrenClick
                ? currentSectionForClick!.child_slides!.filter((child) => child.is_active).sort((a, b) => a.order_index - b.order_index)
                : [] as any[];
            const activeChildrenClickLen = hasChildrenClick ? activeChildrenClick.length : 0;

            if (hasChildrenClick) {
                const atFirstChild = currentChildIndex === 0;
                const atLastChild = currentChildIndex >= activeChildrenClickLen;

                if (mouseX <= threshold && !atFirstChild) {
                    navigateToChild('prev');
                } else if (mouseX >= windowWidth - threshold && !atLastChild) {
                    navigateToChild('next');
                }
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('mousemove', handleMouseMove);
            container.addEventListener('click', handleClick);
        }

        return () => {
            if (container) {
                container.removeEventListener('mousemove', handleMouseMove);
                container.removeEventListener('click', handleClick);
            }
            setCursorType('default');
        };
    }, [sections, currentSectionIndex, currentChildIndex, setCursorType, navigateToChild, navigateToSection, navigate]);

    const renderSlide = (slide: Slide, isActive: boolean) => {
        switch (slide.layout_type) {
            case 'full-media':
                return <FullMediaSection slide={slide} isActive={isActive} onZoomChange={handleZoomChange} />;
            case 'text-section':
                return <TextSection slide={slide} isActive={isActive} />;
            case 'custom-media':
                return <CustomMediaSection slide={slide} isActive={isActive} />;
            case 'split-section':
                return <SplitSection slide={slide} isActive={isActive} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100vh',
                        backgroundColor: '#fff',
                        zIndex: 9998
                    }}
                />
                <Box
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 9999,
                        fontFamily: "'Times New Roman', serif",
                        fontSize: '24px',
                        color: '#000'
                    }}
                >
                    Loading
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-block',
                            width: '30px',
                            textAlign: 'left',
                            '@keyframes dots': {
                                '0%': { content: '"."' },
                                '33%': { content: '".."' },
                                '66%': { content: '"..."' },
                                '100%': { content: '"."' }
                            },
                            '&::after': {
                                content: '"."',
                                animation: 'dots 1.5s steps(3, end) infinite'
                            }
                        }}
                    />
                </Box>
            </>
        );
    }

    const currentSection = sections[currentSectionIndex];
    const hasChildren = currentSection?.child_slides && currentSection.child_slides.length > 0;
    const activeChildren = hasChildren
        ? currentSection.child_slides!.filter(child => child.is_active).sort((a, b) => a.order_index - b.order_index)
        : [];

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100vh',
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#000',
                // Hide scrollbar for all browsers
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                '&::-webkit-scrollbar': {
                    display: 'none' // Chrome, Safari, Opera
                }
            }}
        >
            {/* Render all sections */}
            {sections.map((section, sectionIndex) => {
                const isCurrentSection = sectionIndex === currentSectionIndex;
                const hasChildSections = section.child_slides && section.child_slides.length > 0;
                const sectionActiveChildren = hasChildSections
                    ? section.child_slides!.filter(child => child.is_active).sort((a, b) => a.order_index - b.order_index)
                    : [];

                return (
                    <Box
                        key={section.id}
                        ref={(el: HTMLDivElement | null) => { sectionsRefs.current[sectionIndex] = el; }}
                        sx={{
                            width: hasChildSections ? `${(sectionActiveChildren.length + 1) * 100}vw` : '100vw',
                            height: '100vh',
                            display: 'flex',
                            flexDirection: 'row' // HORIZONTAL layout for child slides
                        }}
                    >
                        {/* Main slide */}
                        <Box sx={{ width: '100vw', height: '100vh', flexShrink: 0 }}>
                            {renderSlide(
                                section.slide,
                                isCurrentSection && currentChildIndex === 0
                            )}
                        </Box>

                        {/* Child slides (HORIZONTAL scroll left/right) */}
                        {hasChildSections && sectionActiveChildren.map((child, childIdx) => (
                            <Box
                                key={child.id}
                                sx={{ width: '100vw', height: '100vh', flexShrink: 0 }}
                            >
                                {renderSlide(
                                    child.slide_data,
                                    isCurrentSection && currentChildIndex === childIdx + 1
                                )}
                            </Box>
                        ))}
                    </Box>
                );
            })}

            {/* Horizontal Navigation Arrows removed – navigation via custom cursor only */}

            {/* Navigation Dots for child slides (mobile only) - Horizontal at bottom */}
            {hasChildren && (
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'none' }, // Visible on mobile only
                        flexDirection: 'row', // Horizontal layout
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'fixed',
                        bottom: 20, // Bottom position
                        left: '50%',
                        transform: 'translateX(-50%)', // Horizontally centered
                        zIndex: 1400,
                        gap: 1.5, // Horizontal spacing between dots
                        padding: '8px 16px',
                        mixBlendMode: 'difference',
                        // backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '20px'
                    }}
                >
                    {/* Main slide dot */}
                    <Box
                        onClick={() => {
                            const container = sectionsRefs.current[currentSectionIndex];
                            if (container) {
                                gsap.to(container, {
                                    x: 0,
                                    duration: ANIMATION_CONFIG.childTransition.duration,
                                    ease: ANIMATION_CONFIG.childTransition.ease,
                                    onComplete: () => {
                                        setCurrentChildIndex(0);
                                        setAnimationKey(prev => prev + 1);
                                    }
                                });
                            }
                        }}
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: currentChildIndex === 0 ? '#fff' : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '1px solid #fff',
                            // Larger touch area
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -8,
                                left: -8,
                                right: -8,
                                bottom: -8
                            }
                        }}
                    />

                    {/* Child slide dots */}
                    {activeChildren.map((_, idx) => (
                        <Box
                            key={idx}
                            onClick={() => {
                                const targetIdx = idx + 1;
                                const container = sectionsRefs.current[currentSectionIndex];
                                if (container) {
                                    gsap.to(container, {
                                        x: -targetIdx * window.innerWidth,
                                        duration: ANIMATION_CONFIG.childTransition.duration,
                                        ease: ANIMATION_CONFIG.childTransition.ease,
                                        onComplete: () => {
                                            setCurrentChildIndex(targetIdx);
                                            setAnimationKey(prev => prev + 1);
                                        }
                                    });
                                }
                            }}
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: currentChildIndex === idx + 1 ? '#fff' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: '1px solid #fff',
                                position: 'relative',
                                // Larger touch area
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -8,
                                    left: -8,
                                    right: -8,
                                    bottom: -8
                                }
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default Home;
